import * as THREE from "three";
import type { QualitySettings } from "../perf/QualityManager";
import { Signal } from "../core/Signal";

export interface ResizeEvent {
  width: number;
  height: number;
  pixelRatio: number;
}

export class RendererService {
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly onResize = new Signal<ResizeEvent>();

  private cssWidth = 1;
  private cssHeight = 1;
  private internalScale = 1;
  private dprCap: number;
  private resizeObserver: ResizeObserver | null = null;
  private onWindowResize = (): void => this.measureAndResize();

  constructor(canvas: HTMLCanvasElement, quality: QualitySettings) {
    this.canvas = canvas;
    this.dprCap = quality.pixelRatioCap;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: "high-performance",
      stencil: false,
      depth: true,
      alpha: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    if (quality.tier !== "low") {
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.0;
    } else {
      this.renderer.toneMapping = THREE.NoToneMapping;
    }
    this.renderer.shadowMap.enabled = false;
    this.renderer.setClearColor(0x06070b, 1);

    this.attachResize();
    this.measureAndResize();
  }

  private attachResize(): void {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.measureAndResize());
      this.resizeObserver.observe(this.canvas);
    }
    window.addEventListener("resize", this.onWindowResize);
    window.addEventListener("orientationchange", this.onWindowResize);
  }

  private measureAndResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width || window.innerWidth));
    const h = Math.max(1, Math.floor(rect.height || window.innerHeight));
    if (w === this.cssWidth && h === this.cssHeight) {
      this.applyInternalSize();
      return;
    }
    this.cssWidth = w;
    this.cssHeight = h;
    this.applyInternalSize();
    this.onResize.emit({
      width: w,
      height: h,
      pixelRatio: this.effectivePixelRatio(),
    });
  }

  private applyInternalSize(): void {
    const pr = this.effectivePixelRatio();
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(this.cssWidth, this.cssHeight, false);
  }

  private effectivePixelRatio(): number {
    const dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);
    return dpr * this.internalScale;
  }

  setInternalScale(scale: number): void {
    const clamped = Math.min(Math.max(scale, 0.5), 1.0);
    if (Math.abs(clamped - this.internalScale) < 0.001) return;
    this.internalScale = clamped;
    this.applyInternalSize();
    this.onResize.emit({
      width: this.cssWidth,
      height: this.cssHeight,
      pixelRatio: this.effectivePixelRatio(),
    });
  }

  getInternalScale(): number {
    return this.internalScale;
  }

  getSize(): { width: number; height: number } {
    return { width: this.cssWidth, height: this.cssHeight };
  }

  dispose(): void {
    this.resizeObserver?.disconnect();
    window.removeEventListener("resize", this.onWindowResize);
    window.removeEventListener("orientationchange", this.onWindowResize);
    this.renderer.dispose();
    this.onResize.clear();
  }
}
